import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, CircularProgress } from '@mui/material';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ChatService } from '../../../../services/chatService';

const chatService = new ChatService();

// The rows live in memory on the API only for as long as the conversation does,
// so a reloaded transcript can still show the table while the download is gone.
const ExpiredMessage =
	'This report is no longer available. Please ask for it again.';

const FailedMessage = 'The report could not be exported. Please try again.';

const buildFileName = (title) => {
	const cleaned = String(title || 'Report')
		.replace(/[\\/:*?"<>|]/g, ' ')
		.trim();
	return `${cleaned || 'Report'}.xlsx`;
};

ChatReportTable.propTypes = {
	report: PropTypes.object.isRequired,
};

export default function ChatReportTable({ report }) {
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState(null);

	const columns = report?.columns || [];
	const rows = report?.previewRows || [];

	if (!columns.length) {
		return null;
	}

	const download = async () => {
		setIsExporting(true);
		setError(null);
		try {
			const resp = await chatService.exportReport(report.reportId);
			if (!resp?.data) {
				throw new Error('empty-response');
			}
			saveAs(resp.data, buildFileName(report.title));
		} catch (e) {
			setError(e?.response?.status === 404 ? ExpiredMessage : FailedMessage);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Box className="mt-2 w-full rounded-lg border border-gray-200">
			<Box className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
				<Box className="min-w-0 flex-1">
					<Box className="truncate text-[13px] font-semibold text-gray-800">
						{report.title || 'Report'}
					</Box>
					<Box className="text-[11px] text-gray-500">
						{report.isPreviewTruncated
							? `Showing ${rows.length} of ${report.rowCount} rows`
							: `${report.rowCount} ${
									report.rowCount === 1 ? 'row' : 'rows'
							  }`}
					</Box>
				</Box>
				<Button
					size="small"
					variant="outlined"
					disabled={isExporting}
					onClick={download}
					startIcon={
						isExporting ? (
							<CircularProgress size={14} />
						) : (
							<DownloadIcon sx={{ fontSize: 16 }} />
						)
					}
					sx={{ textTransform: 'none', fontSize: '12px', whiteSpace: 'nowrap' }}>
					Excel
				</Button>
			</Box>

			<Box className="max-h-64 overflow-auto">
				<table className="w-full border-collapse text-[12px]">
					<thead>
						<tr>
							{columns.map((column, columnIndex) => (
								<th
									key={`h${columnIndex}`}
									className="sticky top-0 border-b border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold">
									{column}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={`r${rowIndex}`}>
								{columns.map((column, columnIndex) => (
									<td
										key={`r${rowIndex}c${columnIndex}`}
										className="border-b border-gray-100 px-2 py-1 align-top">
										{row[columnIndex]}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Box>

			{error ? (
				<Box className="border-t border-gray-200 px-3 py-2 text-[11px] text-red-600">
					{error}
				</Box>
			) : null}
		</Box>
	);
}
